import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Award, Eye, RotateCcw, Save } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { formatDateTime } from '../../lib/dateFmt';
import { Button } from '../../components/ui/Button';
import { SkeletonList } from '../../components/ui/Skeleton';
import { api, ApiError } from '../../lib/api';

interface PdfTemplate {
  key: string;
  heading: string;
  mobilisation_name: string;
  body_paragraph: string;
  footer_text: string;
  updated_at: string;
  updated_by_user_id: string | null;
}

const ALLOWED = ['admin', 'super_admin'];

/**
 * Placeholders each template substitutes at render time. Drives the
 * field hints so operators don't paste `{{completion_date}}` into a
 * receipt (which only knows `{{station}}` / `{{date}}`).
 */
const PLACEHOLDER_HINTS: Record<string, { body: string[]; footer: string[] }> = {
  completion_certificate: {
    body: ['{{completion_date}}'],
    footer: ['{{reference}}'],
  },
  kit_issuance_receipt: {
    body: ['{{station}}', '{{date}}'],
    footer: ['{{station}}', '{{reference}}'],
  },
  signed_form: {
    body: ['{{form_title}}'],
    footer: ['{{form_title}}', '{{reference}}'],
  },
};

const DEFAULT_HINTS = { body: ['{{reference}}'], footer: ['{{reference}}'] };

function hintsFor(key: string) {
  return PLACEHOLDER_HINTS[key] ?? DEFAULT_HINTS;
}

/** Render a comma-separated run of <code> placeholder chips. */
function Placeholders({ tokens }: { tokens: string[] }) {
  return (
    <>
      {tokens.map((t, i) => (
        <span key={t}>
          {i > 0 ? ' and ' : ''}
          <code className="font-mono">{t}</code>
        </span>
      ))}
    </>
  );
}

/**
 * Edit the strings printed inside the certificate PDF. The pdfkit
 * layout is fixed; these fields are the bits the operator actually
 * wants to change (mobilisation name, body copy, footer text).
 *
 * Saving writes to pdf_templates; existing sent certificates keep
 * their snapshotted PDFs. Use the "Re-render" button on a person's
 * record to overwrite a specific snapshot with the new template.
 */
export function PdfTemplates() {
  const { data: user, isLoading } = useCurrentUser();
  const [templates, setTemplates] = useState<PdfTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(
    'completion_certificate',
  );

  useEffect(() => {
    if (!user || !user.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ rows: PdfTemplate[] }>('/api/admin/pdf-templates');
        if (!cancelled) setTemplates(r.rows);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const selected = useMemo(
    () => templates?.find((t) => t.key === selectedKey) ?? null,
    [templates, selectedKey],
  );

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        PDF templates is restricted to admin / super_admin.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Award className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              PDF templates
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Edit the strings printed inside the generated PDFs (completion
              certificate, kit issuance receipt). The layout (frame, fonts,
              positions) is fixed; the wording isn&apos;t. Each field lists
              the placeholders it understands below the input.
            </p>
          </div>
        </header>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="mt-6 grid gap-4 lg:grid-cols-[220px_1fr]">
          <aside className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
            {templates === null ? (
              <div className="p-4">
                <SkeletonList rows={2} />
              </div>
            ) : (
              <ul className="divide-y divide-divider/10">
                {templates.map((t) => (
                  <li key={t.key}>
                    <button
                      type="button"
                      onClick={() => setSelectedKey(t.key)}
                      className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left transition-colors ${
                        selectedKey === t.key
                          ? 'bg-accent/10 text-accent'
                          : 'hover:bg-surface-2'
                      }`}
                    >
                      <span className="text-sm font-medium">{t.key}</span>
                      <span className="text-[11px] text-fg-muted line-clamp-1">
                        {t.heading}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section>
            {selected ? (
              <PdfTemplateEditor
                template={selected}
                onSaved={(updated) =>
                  setTemplates((prev) =>
                    prev?.map((x) => (x.key === updated.key ? updated : x)) ?? prev,
                  )
                }
              />
            ) : (
              <div className="rounded-lg border border-dashed border-line-strong/20 bg-surface p-8 text-center text-sm text-fg-muted">
                Pick a template on the left.
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

function PdfTemplateEditor({
  template,
  onSaved,
}: {
  template: PdfTemplate;
  onSaved: (t: PdfTemplate) => void;
}) {
  const [heading, setHeading] = useState(template.heading);
  const [mobilisationName, setMobilisationName] = useState(template.mobilisation_name);
  const [bodyParagraph, setBodyParagraph] = useState(template.body_paragraph);
  const [footerText, setFooterText] = useState(template.footer_text);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHeading(template.heading);
    setMobilisationName(template.mobilisation_name);
    setBodyParagraph(template.body_paragraph);
    setFooterText(template.footer_text);
    setPreviewUrl(null);
    setPreviewError(null);
  }, [template]);

  // Revoke any previously-created blob URL when this component
  // unmounts or replaces it with a new preview.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const dirty =
    heading !== template.heading ||
    mobilisationName !== template.mobilisation_name ||
    bodyParagraph !== template.body_paragraph ||
    footerText !== template.footer_text;

  async function preview() {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await fetch(
        `/api/admin/pdf-templates/${template.key}/preview`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            heading,
            mobilisation_name: mobilisationName,
            body_paragraph: bodyParagraph,
            footer_text: footerText,
          }),
        },
      );
      if (!res.ok) {
        const t = await res.text();
        setPreviewError(t || `preview failed (${res.status})`);
        setPreviewing(false);
        return;
      }
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      requestAnimationFrame(() => {
        previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'preview failed');
    } finally {
      setPreviewing(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const r = await api<{ template: PdfTemplate }>(
        `/api/admin/pdf-templates/${template.key}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            heading,
            mobilisation_name: mobilisationName,
            body_paragraph: bodyParagraph,
            footer_text: footerText,
          }),
        },
      );
      onSaved(r.template);
      toast.success(`Saved ${template.key}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  function revert() {
    setHeading(template.heading);
    setMobilisationName(template.mobilisation_name);
    setBodyParagraph(template.body_paragraph);
    setFooterText(template.footer_text);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-line/10 bg-surface p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
              {template.key}
            </div>
            <div className="mt-0.5 text-[11px] text-fg-dim">
              Last saved {formatDateTime(template.updated_at)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
              onClick={revert}
              disabled={!dirty || saving}
            >
              Revert
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Eye className="h-3.5 w-3.5" />}
              loading={previewing}
              onClick={preview}
            >
              Preview
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Save className="h-3.5 w-3.5" />}
              loading={saving}
              disabled={!dirty}
              onClick={save}
            >
              Save
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Heading</span>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Mobilisation name</span>
            <input
              type="text"
              value={mobilisationName}
              onChange={(e) => setMobilisationName(e.target.value)}
              className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>
        </div>

        <label className="mt-4 block text-sm">
          <span className="font-medium text-fg-muted">Body paragraph</span>
          <textarea
            value={bodyParagraph}
            onChange={(e) => setBodyParagraph(e.target.value)}
            rows={4}
            className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
          <span className="mt-1 block text-[11px] text-fg-muted">
            Placeholders: <Placeholders tokens={hintsFor(template.key).body} />.
          </span>
        </label>

        <label className="mt-4 block text-sm">
          <span className="font-medium text-fg-muted">Footer text</span>
          <input
            type="text"
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
          <span className="mt-1 block text-[11px] text-fg-muted">
            Placeholders: <Placeholders tokens={hintsFor(template.key).footer} />.
          </span>
        </label>
      </div>

      <div
        ref={previewRef}
        className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm scroll-mt-4"
      >
        <header className="flex items-center gap-2 border-b border-line/10 bg-surface-2/60 px-4 py-2.5">
          <Eye className="h-4 w-4 text-fg-muted" />
          <span className="text-sm font-semibold">Preview</span>
          <span className="ml-auto text-[11px] text-fg-muted">
            Rendered against a sample attendee.
          </span>
        </header>
        {previewError ? (
          <div className="border-b border-danger/25 bg-danger-soft/15 px-4 py-2 text-xs text-danger-soft-fg">
            {previewError}
          </div>
        ) : null}
        {previewUrl ? (
          <iframe
            src={previewUrl}
            title="PDF preview"
            className="h-[60vh] w-full"
          />
        ) : (
          <div className="px-4 py-8 text-center text-sm text-fg-muted">
            Click <strong>Preview</strong> to render the PDF against a sample
            attendee.
          </div>
        )}
      </div>
    </div>
  );
}
